/**
 * Reference-line lookup (03 §3, schema README "Reference-line lookup"): official distance s (+ lateral
 * offset) → world xy on the v6 reference line, exactly as `common.RefTrack.xy_at`:
 * sort the points by official s (periodic in lap_m), find the bracket (searchsorted right − 1), lerp xy and
 * the normal, re-normalize the normal, add normal · lat. All in f64.
 *
 * O(1): a uniform 0.25 m table over [0, lap_m) stores the bracket index at each bin start; a query walks
 * at most a couple of points forward, so results are identical to the binary search.
 */
import type { Manifest, Track } from '../api.ts';
import type { Corner, Zone } from '../bundle/manifest.gen.ts';
import { pyMod } from '../bundle/timeline.ts';

export const LUT_STEP_M = 0.25;

export interface TrackSource {
  refXY: Float32Array;    // N×2
  refS: Float32Array;     // N
  refNrm: Float32Array;   // N×2
}

export interface CornerInfo { turn: number; name: string; short: string }

export class TrackLut implements Track {
  readonly length: number;
  /** extended sorted arrays: index 0 = last point − lap, index N+1 = first point + lap (f64) */
  readonly s: Float64Array;
  readonly px: Float64Array;
  readonly py: Float64Array;
  readonly nx: Float64Array;
  readonly ny: Float64Array;
  private readonly lut: Int32Array;
  private readonly zones: readonly Zone[];
  private readonly corners: readonly Corner[];
  /** corner window (m, ±) for cornerAt */
  cornerWindowM = 120;

  constructor(src: TrackSource, lapM: number, meta?: { corners: readonly Corner[]; zones: readonly Zone[] }) {
    const n = src.refS.length;
    if (src.refXY.length !== 2 * n || src.refNrm.length !== 2 * n) throw new Error('track: ref array sizes differ');
    this.length = lapM;
    const idx = Array.from({ length: n }, (_, i) => i);
    idx.sort((a, b) => (src.refS[a] ?? 0) - (src.refS[b] ?? 0) || a - b);   // stable argsort
    const E = n + 2;
    this.s = new Float64Array(E);
    this.px = new Float64Array(E);
    this.py = new Float64Array(E);
    this.nx = new Float64Array(E);
    this.ny = new Float64Array(E);
    for (let k = 0; k < n; k++) {
      const i = idx[k] ?? 0;
      this.s[k + 1] = src.refS[i] ?? 0;
      this.px[k + 1] = src.refXY[2 * i] ?? 0;
      this.py[k + 1] = src.refXY[2 * i + 1] ?? 0;
      this.nx[k + 1] = src.refNrm[2 * i] ?? 0;
      this.ny[k + 1] = src.refNrm[2 * i + 1] ?? 0;
    }
    const copy = (to: number, from: number, ds: number): void => {
      this.s[to] = (this.s[from] ?? 0) + ds;
      this.px[to] = this.px[from] ?? 0;
      this.py[to] = this.py[from] ?? 0;
      this.nx[to] = this.nx[from] ?? 0;
      this.ny[to] = this.ny[from] ?? 0;
    };
    copy(0, n, -lapM);
    copy(n + 1, 1, lapM);
    // bracket index (searchsorted right − 1, clipped to [0, E−2]) at every bin start
    const nb = Math.ceil(lapM / LUT_STEP_M) + 1;
    this.lut = new Int32Array(nb);
    let k = 0;
    for (let b = 0; b < nb; b++) {
      const sb = b * LUT_STEP_M;
      while (k + 1 < E && (this.s[k + 1] ?? 0) <= sb) k++;
      this.lut[b] = Math.min(k, E - 2);
    }
    this.zones = meta?.zones ?? [];
    this.corners = meta?.corners ?? [];
  }

  static fromBundle(b: { manifest: Manifest; track: TrackSource }): TrackLut {
    return new TrackLut(b.track, b.manifest.track.lap_m, b.manifest.track);
  }

  /** bracket k and weight w for official s (already wrapped into [0, lap)) */
  private bracket(sw: number): number {
    const E = this.s.length;
    let k = this.lut[Math.floor(sw / LUT_STEP_M)] ?? 0;
    while (k + 2 < E && (this.s[k + 1] ?? 0) <= sw) k++;
    return k;
  }

  /**
   * world position of official s with lateral offset lat into out[o], out[o+1] (f64 math; v6 RefTrack.xy_at).
   * Also usable with a Float64Array scratch for exact downstream math.
   */
  place(s: number, lat: number, out: Float32Array | Float64Array | number[], o = 0): void {
    const sw = pyMod(s, this.length);
    const k = this.bracket(sw);
    const s0 = this.s[k] ?? 0, s1 = this.s[k + 1] ?? 0;
    const w = (sw - s0) / (s1 - s0);
    const w1 = 1 - w;
    const x = (this.px[k] ?? 0) * w1 + (this.px[k + 1] ?? 0) * w;
    const y = (this.py[k] ?? 0) * w1 + (this.py[k + 1] ?? 0) * w;
    let nx = (this.nx[k] ?? 0) * w1 + (this.nx[k + 1] ?? 0) * w;
    let ny = (this.ny[k] ?? 0) * w1 + (this.ny[k + 1] ?? 0) * w;
    const nn = Math.sqrt(nx * nx + ny * ny);
    nx /= nn;
    ny /= nn;
    out[o] = x + nx * lat;
    out[o + 1] = y + ny * lat;
  }

  xy(s: number, out: Float32Array | number[]): void {
    this.place(s, 0, out, 0);
  }

  nrm(s: number, out: Float32Array | number[]): void {
    const sw = pyMod(s, this.length);
    const k = this.bracket(sw);
    const w = (sw - (this.s[k] ?? 0)) / ((this.s[k + 1] ?? 0) - (this.s[k] ?? 0));
    const nx = (this.nx[k] ?? 0) * (1 - w) + (this.nx[k + 1] ?? 0) * w;
    const ny = (this.ny[k] ?? 0) * (1 - w) + (this.ny[k + 1] ?? 0) * w;
    const nn = Math.sqrt(nx * nx + ny * ny);
    out[0] = nx / nn;
    out[1] = ny / nn;
  }

  /** unit direction of travel: the left normal rotated clockwise by 90° */
  tangent(s: number, out: Float32Array | number[]): void {
    this.nrm(s, out);
    const nx = out[0] ?? 0, ny = out[1] ?? 0;
    out[0] = ny;
    out[1] = -nx;
  }

  /** v6 zone_at(s): the named zone containing s (first zone if none) — its short name */
  zoneAt(s: number): string {
    return this.zone(s)?.short ?? '';
  }

  zone(s: number): Zone | null {
    const sw = pyMod(s, this.length);
    for (const z of this.zones) if (z.s0 <= sw && sw < z.s1) return z;
    return this.zones[0] ?? null;
  }

  /** v6 nearest_turn(s): [turn, signed metres from that corner marker] (null without corners) */
  nearestTurn(s: number): { corner: Corner; d: number } | null {
    const L = this.length;
    const sw = pyMod(s, L);
    let best: { corner: Corner; d: number } | null = null;
    for (const c of this.corners) {
      const d = pyMod(sw - c.s + L / 2, L) - L / 2;
      if (best === null || Math.abs(d) < Math.abs(best.d)) best = { corner: c, d };
    }
    return best;
  }

  /** the nearest corner within ±cornerWindowM of s, else null */
  cornerAt(s: number): CornerInfo | null {
    const n = this.nearestTurn(s);
    if (!n || Math.abs(n.d) > this.cornerWindowM) return null;
    return { turn: n.corner.turn, name: n.corner.name, short: n.corner.short };
  }
}
